import Render from 'components/Common/Render';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RootState } from 'store';
import { useLazyBusinessListQuery } from 'store/apis/businessListApi';
import { setBusiness, setBusinesses } from 'store/slices/businessSlice';
import { ReactComponent as EmptyLogo } from 'assets/empty_business_logo_square.svg';
import { Loader } from 'components/Common/Loader';
import { checkIsAnywhereCommunity } from 'utils/common';
import SelectionList from 'components/Common/SelectionList';
import { BusinessStatus } from 'enum/common';
import Badge from 'components/Common/Badge';
import useMixpanel from 'hooks/useMixpanel';
import { useLazyDefaultAccountQuery } from 'store/apis/defaultAccountApi';
import { setDefaultAccount } from 'store/slices/defaultAccountSlice';
import { routes } from 'constants/routes';
import Button from 'components/Buttons/Button';
interface IChooseBusinessType {}

const ChooseBusiness: React.FC<IChooseBusinessType> = () => {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const activateInstantPay = searchParams.get('activateInstantPay');
  const navigationUrl = searchParams.get('navigationUrl');
  const fromOnboarding = searchParams.get('fromOnboarding');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { trackEvent } = useMixpanel();

  const [fetchBusinessses, { data, isFetching, isSuccess }] = useLazyBusinessListQuery();
  const businessList = useAppSelector((state: RootState) => state?.businesses?.businesses);
  const [
    fetchDefaultAccount,
    {
      data: defaultPaymentMethod,
      isLoading: isFetchingPaymentMethod,
      isSuccess: isAccountsetupSuccess,
    },
  ] = useLazyDefaultAccountQuery({});

  const [selectedBusiness, setSelectedBusiness] = useLocalStorage('selectedBusiness', null);

  const renderBusinessDetails = (business: EntityType) => {
    const isInactive = business?.business?.businessStatus === BusinessStatus.IN_ACTIVE;
    return (
      <div className={`px-5 flex flex-row `}>
        <div>
          {business?.business?.logo ? (
            <img
              src={business?.business?.logo}
              className="inline-block h-12 w-12 rounded-full ring-2 ring-white"
            />
          ) : (
            <EmptyLogo className="inline-block h-12 w-12 rounded-full ring-2 ring-white" />
          )}
        </div>
        <div className="ml-3 justify-center flex flex-col">
          <div className="text-darkBlue text-lg">
            {business?.business?.name}
            {isInactive && <Badge label="Pending Approval" customClass="ml-2" />}
          </div>
          {!checkIsAnywhereCommunity(business?.business) && (
            <div className="text-lightGrey">{business?.business?.communityName} </div>
          )}
        </div>
      </div>
    );
  };

  const businessOptions = useMemo(
    () =>
      businessList?.map((business: EntityType) => ({
        label: renderBusinessDetails(business),
        value: business?.business?.uuid || '',
        data: business,
      })) || [],
    [businessList],
  );

  const handleBusinessChange = (uuid: string, myBusinesses: EntityType[] = businessList || []) => {
    trackEvent('select_business_from_list', { businessId: uuid });
    const filteredBusiness = myBusinesses?.filter(
      (business: EntityType) => business?.business?.uuid === uuid,
    )[0];
    // Only fetch if UUID exists and is not empty
    if (uuid && uuid.trim()) {
      fetchDefaultAccount({ uuid });
    }
    if (filteredBusiness) {
      setSelectedBusiness({ ...filteredBusiness?.business });
      dispatch(setBusiness({ ...filteredBusiness?.business }));
      // Decides where to route if user coming from mobile app
      if (!!navigationUrl) {
        navigate(`/${navigationUrl}`);
      } else {
        navigate('/', {
          state: {
            isBusinessRefreshed: true,
            activateInstantPay: activateInstantPay === 'true',
          },
        });
      }
    } else if (fromOnboarding === 'true' && uuid && uuid.trim()) {
      // If coming from onboarding but business not found in list yet,
      // navigate to home anyway - the home page will handle the missing business case
      navigate('/', {
        state: {
          isBusinessRefreshed: true,
          activateInstantPay: activateInstantPay === 'true',
          fromOnboarding: true,
        },
      });
    }
  };

  useEffect(() => {
    if (businessId !== '1' && !isFetching) {
      handleBusinessChange(businessId || '');
    }
  }, [isFetching, businessId, businessList, activateInstantPay]);

  useEffect(() => {
    fetchBusinessses();
  }, []);

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(setBusinesses(data || []));
    }
    // Auto-select only if there's exactly one business AND user is NOT explicitly viewing the page
    // businessId === "1" means user clicked "Switch Business" to view the page
    if (data && data?.length === 1 && businessId !== '1') {
      handleBusinessChange(data[0]?.business?.uuid || '', data);
    }
    // Redirect to onboarding if no businesses found
    // BUT NOT if we just came from onboarding (to prevent redirect loop)
    if (isSuccess && data && data?.length === 0 && fromOnboarding !== 'true') {
      navigate(routes.onboardingBusiness);
    }
    // If coming from onboarding but no businesses found, navigate to home with a message
    if (isSuccess && data && data?.length === 0 && fromOnboarding === 'true') {
      navigate(routes.home);
    }
  }, [data, isSuccess, fromOnboarding, businessId]);

  useEffect(() => {
    if (isAccountsetupSuccess && defaultPaymentMethod) {
      dispatch(setDefaultAccount(defaultPaymentMethod));
    }
  }, [defaultPaymentMethod, isAccountsetupSuccess]);

  const containerStyle = 'bg-offWhite h-screen overflow-hidden';

  return (
    <div className={containerStyle}>
      {/* Full width header */}
      <div className="px-4 md:px-8 py-4">
        <div className="flex justify-between items-center gap-2">
          <h2 className="font-medium text-2xl md:text-3xl text-darkBlue whitespace-nowrap">
            Choose Business
          </h2>
          {/* Desktop only button */}
          <Button
            label="+ Add New Business"
            onClick={() => navigate(routes.onboardingBusiness)}
            customClass="hidden md:block !w-auto !h-[40px] !px-4 !mb-0 !rounded-full !text-sm !whitespace-nowrap"
          />
        </div>
        <p className="text-black60 font-regular text-sm mt-1">
          We found the following business profiles that are matched to your email in our system
        </p>
      </div>

      {/* Full width content - adjusted height for mobile bottom button */}
      <div className="px-4 md:px-8 pb-[80px] md:pb-0">
        <div
          className={`bg-white p-4 h-[calc(100vh-200px)] md:h-[80vh] no-scrollbar ${
            isFetching ? 'overflow-hidden' : 'overflow-scroll'
          }`}
        >
          <Render when={!isFetching && !businessOptions?.length}>
            <div className="justify-center flex items-center h-full">No Businesses Found</div>
          </Render>
          {isFetching ? (
            <div className="flex justify-center items-center h-full">
              <Loader size={8} color={'darkBlue'} />
            </div>
          ) : (
            <SelectionList
              onChange={(option) => {
                handleBusinessChange(option.value);
              }}
              options={businessOptions}
              containerClass={'text-sm'}
            />
          )}
        </div>
      </div>

      {/* Mobile bottom button - fixed at bottom */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-borderGrey px-4 py-4 flex justify-center">
        <Button
          label="+Add Business"
          onClick={() => navigate(routes.onboardingBusiness)}
          customClass="!w-[335px] !max-w-full !h-[48px] !mb-0 !rounded-[8px] !text-sm"
        />
      </div>
    </div>
  );
};

export default ChooseBusiness;
